<template>
  <el-dialog v-model="visible" title="신규 LOT 등록" width="600px" destroy-on-close>
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="법인" prop="company">
            <el-select v-model="form.company" filterable clearable placeholder="법인 선택" style="width: 100%">
              <el-option v-for="c in codeStore.ourCompanys" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="LOT번호" prop="lot_number">
            <el-input v-model="form.lot_number" placeholder="예: LOT-2024-001" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="자재유형" prop="material_type">
            <el-select v-model="form.material_type" style="width: 100%">
              <el-option label="다운" value="DOWN" />
              <el-option label="원단" value="FABRIC" />
              <el-option label="부자재" value="ACCESSORY" />
              <el-option label="기타" value="OTHER" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="자재명" prop="material_name">
        <el-input v-model="form.material_name" placeholder="자재명" />
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="수량" prop="quantity">
            <el-input-number v-model="form.quantity" :min="0" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="단위" prop="unit">
            <el-input v-model="form.unit" placeholder="kg, m, ea" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="총금액" prop="total_amount">
            <el-input-number v-model="form.total_amount" :min="0" :precision="2" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="통화" prop="currency">
            <el-select v-model="form.currency" style="width: 100%">
              <el-option label="USD" value="USD" />
              <el-option label="KRW" value="KRW" />
              <el-option label="CNY" value="CNY" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="공급업체">
        <el-select v-model="form.supplier" placeholder="공급업체 선택" clearable style="width: 100%">
          <el-option
            v-for="supplier in suppliers"
            :key="supplier.supplier_id"
            :label="supplier.name"
            :value="supplier.supplier_id"
          />
        </el-select>
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="보관창고">
            <el-select v-model="form.warehouse" placeholder="창고 선택" clearable style="width: 100%">
              <el-option
                v-for="wh in warehouses"
                :key="wh.id"
                :label="`${wh.warehouse_type_display} - ${wh.name}`"
                :value="wh.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="구매발주번호">
            <el-input v-model="form.purchase_order_number" placeholder="PO번호" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="발주일" prop="order_date">
            <el-date-picker
              v-model="form.order_date"
              type="date"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="유효기한">
            <el-date-picker
              v-model="form.expiry_date"
              type="date"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="비고">
        <el-input v-model="form.remarks" type="textarea" :rows="3" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="visible = false">취소</el-button>
      <el-button type="primary" @click="handleSubmit">저장</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const codeStore = useCodeStore()
const visible = ref(props.modelValue)
const formRef = ref(null)
const suppliers = ref([])
const warehouses = ref([])

const form = reactive({
  lot_number: '',
  company: null,
  material_type: 'DOWN',
  material_name: '',
  quantity: 0,
  unit: 'kg',
  total_amount: 0,
  currency: 'USD',
  supplier: null,
  warehouse: null,
  purchase_order_number: '',
  order_date: new Date().toISOString().split('T')[0],
  expiry_date: null,
  remarks: ''
})

const rules = {
  lot_number: [{ required: true, message: 'LOT번호를 입력하세요', trigger: 'blur' }],
  material_name: [{ required: true, message: '자재명을 입력하세요', trigger: 'blur' }],
  quantity: [{ required: true, message: '수량을 입력하세요', trigger: 'blur' }],
  total_amount: [{ required: true, message: '총금액을 입력하세요', trigger: 'blur' }]
}

const fetchSuppliers = async () => {
  try {
    const response = await axiosInstance.get('suppliers/')
    suppliers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch suppliers:', error)
  }
}

const fetchWarehouses = async () => {
  try {
    const response = await axiosInstance.get('warehouses/', { params: { is_active: true } })
    warehouses.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch warehouses:', error)
  }
}

const handleSubmit = async () => {
  await formRef.value.validate()

  try {
    await axiosInstance.post('material-lots/', form)
    ElMessage.success('LOT가 등록되었습니다.')
    emit('saved')
    visible.value = false
  } catch (error) {
    ElMessage.error('저장에 실패했습니다.')
    console.error(error)
  }
}

watch(() => props.modelValue, (val) => {
  visible.value = val
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

onMounted(() => {
  fetchSuppliers()
  fetchWarehouses()
  codeStore.fetchOurCompanys()
})
</script>
